// Pure helpers shared by the Quality tab and the printed worksheet. They live
// here rather than in QualityTab so the printer can use them without the two
// modules importing each other.

// Whether a single check has been acted on (a reading, a pass/fail, or a note).
export const checkDone = (c) => {
  if (c.type === 'measured') return c.actual !== null && c.actual !== undefined;
  if (c.type === 'text') return !!(c.note && String(c.note).trim());
  return !!c.result; // multistate
};

export const checkFailed = (c) => c.result === 'out_of_spec' || c.result === 'fail';

// Checks come in two tiers. Core is the short list a mechanic must answer to
// close the order; extended is the deeper coverage, collapsed by default.
// Checks generated before tiering have no `tier`, so they read as core — that's
// what lets tiering ship without a backfill.
export const isCore = (c) => c.tier !== 'extended';

// What a service's status is allowed to depend on. Extended checks stay out of
// the roll-up until somebody actually answers one; after that an extended
// failure still has to surface.
export const countsToward = (c) => isCore(c) || checkDone(c);

// Which service line(s) a check covers. Vehicle-level checks cover several lines
// via serviceIds; service/legacy checks fall back to the single serviceId.
export const checkServiceIds = (c) => (c.serviceIds && c.serviceIds.length ? c.serviceIds : (c.serviceId ? [c.serviceId] : []));

// Roll-up status for a service from its checks. Filtering happens here rather
// than at the call sites so every consumer — the section chips, the header
// summary, and the SO modal's completion gate — agrees on what counts.
export const serviceRollup = (serviceChecks) => {
  if (!serviceChecks || serviceChecks.length === 0) return 'none';
  // A service covered only by unanswered extended checks would otherwise read
  // 'none' and count as unchecked, so `allClear` could never go true.
  const scoped = serviceChecks.filter(countsToward);
  const considered = scoped.length > 0 ? scoped : serviceChecks;
  if (considered.some(c => c.stale)) return 'needs_recheck';
  if (considered.some(checkFailed)) return 'failed';
  if (considered.every(checkDone)) return 'passed';
  return 'in_progress';
};

// SO-level quality summary, derived from the order's current (non-declined)
// services and their checks. The SO modal's completion gate reuses this so the
// warning it shows matches what the tab displays. `allClear` means every current
// service has a passing, non-stale checklist.
export const computeQualitySummary = (order) => {
  const checks = order.qualityChecks || [];
  const services = (order.services || []).filter(s => !s.declined);
  const byService = {};
  for (const c of checks) for (const sid of checkServiceIds(c)) (byService[sid] = byService[sid] || []).push(c);

  let passed = 0, failed = 0, needsRecheck = 0, incomplete = 0, unchecked = 0;
  for (const s of services) {
    const st = serviceRollup(byService[s.id]);
    if (st === 'passed') passed++;
    else if (st === 'failed') failed++;
    else if (st === 'needs_recheck') needsRecheck++;
    else if (st === 'in_progress') incomplete++;
    else unchecked++;
  }
  const total = services.length;
  return { total, passed, failed, needsRecheck, incomplete, unchecked, allClear: total > 0 && passed === total };
};
